import { useEffect, useMemo, useState } from 'react'
import type { Unsubscribe } from 'firebase/firestore'
import type { ChecklistWorkflowStatus } from '../../types/schema'
import { useAppDataRefreshSignal } from '../../hooks/useAppDataRefreshSignal'
import {
  subscribeHelperAssignedItemsForWorkspace,
  type HelperAssignedItem,
  type WorkspaceMaskLookup,
} from './helperAssignedItemsService'
import { subscribeHelperStore, getHelperSnapshot } from './helperStore'

/**
 * Phase 2 — canonical client-side state machine for helper-driven transitions.
 * Each key is the current workflowStatus; the array enumerates the statuses a
 * helper is permitted to transition INTO from that state. Items may override
 * this via an `allowedTransitions` field on the document; absence means the
 * default below applies. Firestore rules treat the document field as the
 * authoritative source and fall back to this same default.
 */
/**
 * Manual (dropdown-driven) transitions available to a helper. ASSESSING is
 * **not** reachable manually — proof upload is the only way in. From
 * ASSESSING a helper can pull their submission back to ONGOING, but the
 * ASSESSING → COMPLETED edge is gated by whether an approver is set: with
 * an approver, only the approver may complete; without one, the completion
 * happens automatically on upload (fast path). Either way the manual
 * dropdown never offers COMPLETED from ASSESSING to a plain helper.
 */
export const DEFAULT_HELPER_TRANSITIONS: Readonly<Record<ChecklistWorkflowStatus, readonly ChecklistWorkflowStatus[]>> = {
  CREATED: [],
  ASSIGNED: ['ACCEPTED', 'NOT_REQUIRED'],
  ACCEPTED: ['ONGOING', 'POSTPONED'],
  ONGOING: ['POSTPONED'],
  ASSESSING: ['ONGOING'],
  POSTPONED: ['ACCEPTED', 'ONGOING'],
  COMPLETED: [],
  NOT_REQUIRED: [],
}

export const ACTIVE_HELPER_STATUSES: ReadonlyArray<ChecklistWorkflowStatus> = [
  'ASSIGNED',
  'ACCEPTED',
  'ONGOING',
  'ASSESSING',
]

export const resolveAllowedTransitions = (item: {
  workflowStatus?: ChecklistWorkflowStatus | null
  allowedTransitions?: Partial<Record<ChecklistWorkflowStatus, ChecklistWorkflowStatus[]>> | null
}): ChecklistWorkflowStatus[] => {
  const status = item.workflowStatus ?? 'ASSIGNED'
  const override = item.allowedTransitions?.[status]
  if (override && Array.isArray(override)) return [...override]
  return [...(DEFAULT_HELPER_TRANSITIONS[status] ?? [])]
}

const dueDateValue = (item: HelperAssignedItem): number => {
  const candidate = item.scheduleEndDate ?? item.endDate ?? item.scheduleStartDate ?? item.startDate
  if (!candidate) return Number.POSITIVE_INFINITY
  const ms = Date.parse(candidate)
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms
}

export const sortHelperItemsByDue = (items: HelperAssignedItem[]): HelperAssignedItem[] =>
  [...items].sort((a, b) => dueDateValue(a) - dueDateValue(b))

export interface HelperTaskFeedState {
  loading: boolean
  items: HelperAssignedItem[]
  activeWorkspaceId: string | null
}

/**
 * React hook — subscribes to the helper's assigned items and re-filters them
 * by the active workspace whenever the helperStore's activeWorkspaceId changes.
 * Uses the same workspace-scoped query shape as the Member app and filters the
 * helper's assigneeId client-side. That avoids helper accounts depending on a
 * global checklist_items query that may be denied or stale under rules/indexes.
 */
export const useHelperTaskFeed = (
  uid: string | null,
  workspaceMaskLookup?: WorkspaceMaskLookup,
  onError?: (err: unknown) => void,
): HelperTaskFeedState => {
  const [activeWorkspaceId, setActive] = useState<string | null>(
    () => getHelperSnapshot().activeWorkspaceId,
  )
  const [items, setItems] = useState<HelperAssignedItem[]>([])
  const [loading, setLoading] = useState(() => Boolean(uid && activeWorkspaceId))
  const [prevKey, setPrevKey] = useState(() => `${uid ?? ''}::${activeWorkspaceId ?? ''}`)
  const currentKey = `${uid ?? ''}::${activeWorkspaceId ?? ''}`
  if (currentKey !== prevKey) {
    setPrevKey(currentKey)
    setItems([])
    setLoading(Boolean(uid && activeWorkspaceId))
  }
  const refreshToken = useAppDataRefreshSignal()

  useEffect(() =>
    subscribeHelperStore((snapshot) => {
      setActive(snapshot.activeWorkspaceId)
    }),
  [])

  useEffect(() => {
    if (!uid || !activeWorkspaceId) {
      return
    }
    const unsubscribe: Unsubscribe = subscribeHelperAssignedItemsForWorkspace(
      uid,
      activeWorkspaceId,
      (next) => {
        setItems(next)
        setLoading(false)
      },
      workspaceMaskLookup,
      onError,
    )
    return () => unsubscribe()
  }, [activeWorkspaceId, onError, refreshToken, uid, workspaceMaskLookup])

  const sortedItems = useMemo(() => {
    const scoped = activeWorkspaceId
      ? items.filter((item) => item.workspaceId === activeWorkspaceId)
      : []
    return sortHelperItemsByDue(scoped)
  }, [items, activeWorkspaceId])

  return {
    loading,
    items: sortedItems,
    activeWorkspaceId,
  }
}
